export interface OrderStatusConfig {
  color: string;
  label: string;
  icon: string;
  i18nKey?: string;
}

export const ORDER_STATUS_CONFIG: Record<string, OrderStatusConfig> = {
  pending: {
    color: '#FFB74D',
    label: 'Ожидает',
    icon: '⏳',
    i18nKey: 'order_status_pending',
  },
  waiting_approval: {
    color: '#64B5F6',
    label: 'Ожидает подтверждения',
    icon: '⏳',
    i18nKey: 'order_status_waiting_approval',
  },
  assigned: {
    color: '#64B5F6',
    label: 'Назначен',
    icon: '✅',
    i18nKey: 'order_status_assigned',
  },
  in_progress: {
    color: '#A5D6A7',
    label: 'В работе',
    icon: '🔧',
    i18nKey: 'order_status_in_progress',
  },
  completed: {
    color: '#4CAF50',
    label: 'Завершён',
    icon: '✔️',
    i18nKey: 'order_status_completed',
  },
  confirmed: {
    color: '#81C784',
    label: 'Подтверждён',
    icon: '✓',
    i18nKey: 'order_status_confirmed',
  },
  archived: {
    color: '#9E9E9E',
    label: 'Архив',
    icon: '📁',
    i18nKey: 'order_status_archived',
  },
  cancelled: {
    color: '#E57373',
    label: 'Отменён',
    icon: '❌',
    i18nKey: 'order_status_cancelled',
  },
  cancelled_by_customer: {
    color: '#E57373',
    label: 'Отменён клиентом',
    icon: '❌',
    i18nKey: 'status_cancelled_by_customer',
  },
  cancelled_by_admin: {
    color: '#E57373',
    label: 'Отменён администратором',
    icon: '❌',
    i18nKey: 'status_cancelled_by_admin',
  },
};

export const getOrderStatusConfig = (status: string): OrderStatusConfig => {
  return ORDER_STATUS_CONFIG[status] || {
    color: '#9E9E9E',
    label: status,
    icon: '❓',
    i18nKey: `order_status_${status}`,
  };
};

const BACKEND_KEY_MAP: Record<string, string> = {
  'pending_to_cancelled_timeout': 'pending',
  'waiting_approval_to_cancelled_timeout': 'waiting_approval',
  'assigned_to_in_progress_timeout': 'assigned',
  'in_progress_to_completed_timeout': 'in_progress',
  'completed_to_confirmed_timeout': 'completed',
};

const DEFAULT_STATUS_TIMEOUTS: Record<string, number> = {
  pending: 180,
  waiting_approval: 60,
  assigned: 60,
  in_progress: 240,
  completed: 20,
};

/**
 * Конфигурация таймаутов статусов (в минутах)
 * Изначально заполнена значениями по умолчанию, обновляется с бэкенда
 */
export let STATUS_TIMEOUTS: Record<string, number> = { ...DEFAULT_STATUS_TIMEOUTS };

/**
 * Загрузка таймаутов с бэкенда
 */
export async function loadStatusTimeouts(): Promise<void> {
  try {
    const { default: api } = await import('../services/ApiService');
    const response = await api.getStatusTimeouts();
    const backendTimeouts = response.data?.data;
    if (backendTimeouts) {
      const mapped: Record<string, number> = {};
      for (const [key, value] of Object.entries(backendTimeouts)) {
        const frontendKey = BACKEND_KEY_MAP[key];
        if (frontendKey && typeof value === 'number') {
          mapped[frontendKey] = value;
        }
      }
      if (Object.keys(mapped).length > 0) {
        STATUS_TIMEOUTS = { ...DEFAULT_STATUS_TIMEOUTS, ...mapped };
      }
    }
  } catch (e) {
    console.warn('[orderStatusConfig] Failed to load timeouts from backend, using defaults', e);
  }
}

/**
 * Получение таймаута для статуса (в минутах)
 */
export const getStatusTimeout = (status: string): number | null => {
  return STATUS_TIMEOUTS[status] || null;
};

/**
 * Форматирование таймаута в человекочитаемый вид
 */
export const formatTimeout = (minutes: number): string => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} ч ${mins} мин` : `${hours} ч`;
  }
  return `${minutes} мин`;
};

export default ORDER_STATUS_CONFIG;
